import { User, Post, Product, Order, Message, Notification, SubscriptionTier, Analytics, ShippingAddress } from '../context/AppContext';

// Helper for generating initial avatars if needed
const generateAvatar = (seed: string, color: string) => {
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
          <stop offset="100%" style="stop-color:#27272b;stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" fill="url(#grad)"/>
      <text x="50" y="62" font-size="42" text-anchor="middle" fill="white" font-family="Arial" font-weight="600">${seed.charAt(0).toUpperCase()}</text>
    </svg>
  `)}`;
};

// Admin credentials: jalenward@hotmail.com / admin867310
export const adminUser: User = {
  id: 'admin',
  username: 'jalenward',
  displayName: 'Jalen Ward',
  email: 'jalenward@hotmail.com',
  avatar: 'https://onlyjalen-api.jalen1wa.workers.dev/api/media/profile/jalen-avatar.jpg',
  coverImage: 'https://onlyjalen-api.jalen1wa.workers.dev/api/media/profile/jalen-cover.jpg',
  bio: '🎥 Fitness & Lifestyle Creator | Transforming lives through movement and mindset.\n🔥 Exclusive workouts & behind-the-scenes content.\n💪 Let\'s build the best version of you together.',
  isAdmin: true,
  isSubscribed: true,
  subscriptionTier: null,
  subscriptionExpiry: null,
  subscribers: 0,
  posts: 0,
  likes: 0,
  earnings: 0,
  availableBalance: 0,
  pendingBalance: 0,
  location: 'Los Angeles, CA',
  website: 'builtbyai.io',
  joinDate: new Date('2022-01-15'),
  online: true,
  paypalEmail: 'jalenward@hotmail.com',
  shippingAddresses: [],
  wishlist: [],
  notificationPrefs: { newPosts: true, messages: true, orderUpdates: true, newProducts: true, promotions: true }
};

// User credentials for authentication
export const userCredentials = [
  { email: 'jalenward@hotmail.com', password: 'admin867310', userId: 'admin', isAdmin: true },
  { email: 'testuser@example.com', password: 'test123456', userId: 'subscriber1', isAdmin: false },
];

const defaultAddress: ShippingAddress = {
  id: 'addr1',
  name: 'Home',
  street: '742 Evergreen Terrace',
  city: 'Springfield',
  state: 'OR',
  zip: '97403',
  country: 'USA',
  isDefault: true
};

// Generate realistic subscribers
const generateSubscribers = (count: number) => {
  const plans: ('monthly' | 'quarterly' | 'yearly' | 'lifetime')[] = ['monthly', 'quarterly', 'yearly', 'lifetime'];
  const statuses: ('active' | 'pending' | 'cancelled')[] = ['active', 'active', 'active', 'pending', 'cancelled'];
  
  return Array.from({ length: count }).map((_, i) => ({
    id: `sub-${i}`,
    display_name: `User ${i + 1}`,
    email: `user${i + 1}@example.com`,
    avatar: `https://i.pravatar.cc/150?u=sub${i}`,
    is_subscribed: i % 5 !== 3,
    subscription_status: statuses[i % statuses.length],
    plan_type: plans[i % plans.length],
    created_at: new Date(Date.now() - Math.random() * 1000 * 60 * 60 * 24 * 30).toISOString(),
    paid_at: i % 5 !== 3 ? new Date(Date.now() - Math.random() * 1000 * 60 * 60 * 24 * 7).toISOString() : null,
  }));
};

export const mockDetailedSubscribers = generateSubscribers(25);

export const buyerUser: User = {
  id: 'buyer1',
  username: 'fitness_fanatic',
  displayName: 'Jordan Miller',
  email: 'jordan@example.com',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop&q=80',
  coverImage: '',
  bio: 'Always pushing for that one extra rep! 💪',
  isAdmin: false,
  isSubscribed: true,
  subscriptionTier: 'premium',
  subscriptionExpiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  subscribers: 0,
  posts: 0,
  likes: 0,
  earnings: 0,
  availableBalance: 0,
  pendingBalance: 0,
  location: 'Austin, TX',
  joinDate: new Date('2023-06-15'),
  online: true,
  shippingAddresses: [defaultAddress],
  wishlist: [],
  notificationPrefs: { newPosts: true, messages: true, orderUpdates: true, newProducts: true, promotions: false }
};

export const testSubscriber: User = {
  id: 'subscriber1',
  username: 'alex_fit',
  displayName: 'Alex Rivers',
  email: 'testuser@example.com',
  avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&h=400&fit=crop&q=80',
  coverImage: '',
  bio: 'Fitness enthusiast and long-time follower of Jalen\'s journey.',
  isAdmin: false,
  isSubscribed: true,
  subscriptionTier: 'monthly',
  subscriptionExpiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  subscribers: 0,
  posts: 0,
  likes: 0,
  earnings: 0,
  availableBalance: 0,
  pendingBalance: 0,
  location: 'New York, NY',
  website: '',
  joinDate: new Date('2024-01-01'),
  online: false,
  paypalEmail: '',
  shippingAddresses: [],
  wishlist: [],
  notificationPrefs: { newPosts: true, messages: true, orderUpdates: true, newProducts: true, promotions: true }
};

export const mockPosts: Post[] = [];

export const mockProducts: Product[] = [];

export const mockAnalytics: Analytics = {
  totalRevenue: 24560.85,
  subscriptionRevenue: 18450.50,
  productRevenue: 6110.35,
  tipRevenue: 2450.00,
  totalSubscribers: 15420,
  activeSubscribers: 12840,
  totalOrders: 425,
  pendingOrders: 12,
  totalProducts: 15,
  lowStockProducts: 3,
  revenueByMonth: [
    { month: 'Jan', amount: 12500 },
    { month: 'Feb', amount: 15000 },
    { month: 'Mar', amount: 18000 },
    { month: 'Apr', amount: 16500 },
    { month: 'May', amount: 24560 }
  ],
  subscribersByMonth: [
    { month: 'Jan', count: 8000 },
    { month: 'Feb', count: 9500 },
    { month: 'Mar', count: 11000 },
    { month: 'Apr', count: 13500 },
    { month: 'May', count: 15420 }
  ],
  topProducts: [
    { name: 'Performance Hoodie', sales: 154 },
    { name: 'Core Leggings', sales: 87 },
    { name: 'Athlete Cap', sales: 62 }
  ]
};

export const subscriptionTiers: SubscriptionTier[] = [
  {
    id: 'monthly',
    name: '1 Month',
    price: 25,
    features: ['Unlimited access to exclusive photos & videos', 'Direct messaging access']
  },
  {
    id: 'yearly',
    name: '1 Year',
    price: 100,
    features: ['Everything in Monthly', 'Save $200 vs monthly', 'VIP chat access'],
    isPopular: true
  }
];

export const mockOrders: Order[] = [];
export const mockMessages: Message[] = [];
export const mockNotifications: Notification[] = [];
export const mockStories: Post[] = [];
